/**
 * useResolvedPreviewAttachments — pobiera pełną listę załączników rekordu
 * (`GET /api/attachments`, identyczny endpoint/queryKey co `AttachmentsSection`
 * — cache współdzielony gdy oba widgety siedzą na tej samej stronie) i rozwiązuje
 * ją do listy faktycznie pokazywanej przez `DocumentPreviewPanel`, wg reguł
 * `resolvePreviewAttachments` (`preview-source.ts`).
 *
 * Wspólny hook dla dwóch konsumentów:
 *  - `DocumentPreviewContextRenderer` (element inline `document_preview_view`,
 *    źródło konfigurowalne all/primary/latest/field),
 *  - `DocumentPreviewDockHost` (panel dokowany całej strony, zawsze `source:'all'`).
 */

import { useMemo } from 'react';
import type { AttachmentOwnerType, RecordAttachmentDTO } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';
import type { AxiosRequestConfig } from 'axios';

import { api } from '@/lib/api';

import { type DocumentPreviewSourceConfig, resolvePreviewAttachments } from './preview-source';

export interface UseResolvedPreviewAttachmentsOptions extends DocumentPreviewSourceConfig {
  ownerType: AttachmentOwnerType;
  ownerId: string;
  /** Nagłówek `X-Company-Id` jawny (wzorzec §4.8) — patrz `AttachmentsSection`. */
  companyId?: string;
  /** Wartości pól dodatkowych rekordu — tylko istotne dla `source === 'field'`. */
  extValues?: Record<string, unknown>;
  /** `false` wstrzymuje fetch (np. dok wyłączony) — domyślnie `true`. */
  enabled?: boolean;
}

export interface UseResolvedPreviewAttachmentsResult {
  /** Lista już rozwiązana wg źródła — to podajemy do `DocumentPreviewPanel`. */
  attachments: RecordAttachmentDTO[];
  /** Pełna lista rekordu, przed filtrowaniem źródła (np. do liczników/diagnostyki). */
  allAttachments: RecordAttachmentDTO[];
  isLoading: boolean;
  isError: boolean;
}

export function useResolvedPreviewAttachments(
  options: UseResolvedPreviewAttachmentsOptions,
): UseResolvedPreviewAttachmentsResult {
  const { ownerType, ownerId, companyId, source, fieldSlug, extValues, enabled = true } = options;

  const query = useQuery({
    queryKey: ['attachments', ownerType, ownerId],
    queryFn: async () => {
      const config: AxiosRequestConfig = { params: { ownerType, ownerId } };
      if (companyId) config.headers = { 'X-Company-Id': companyId };
      const res = await api.get<{ attachments: RecordAttachmentDTO[] }>('/attachments', config);
      return res.data.attachments;
    },
    enabled: enabled && !!ownerId,
  });

  // `useMemo` (nie `?? []` inline) — bez tego `allAttachments` byłaby nową referencją
  // tablicy przy KAŻDYM renderze gdy `query.data` jest `undefined` (przed pierwszym
  // fetchem), co unieważniałoby memoizację `attachments` poniżej (exhaustive-deps).
  const allAttachments = useMemo(() => query.data ?? [], [query.data]);

  const attachments = useMemo(
    () => resolvePreviewAttachments({ source, fieldSlug }, allAttachments, extValues),
    [allAttachments, source, fieldSlug, extValues],
  );

  return { attachments, allAttachments, isLoading: query.isLoading, isError: query.isError };
}
